// @noflow

import React, {useState, useEffect, useRef} from 'react';
import classNames from 'classnames';
import Box from 'sail/Box';
import Group from 'sail/Group';
import {ButtonLink} from 'sail/Button';
import {UnstyledLink} from 'sail/UnstyledLink';
import {Text} from 'sail/Text';
import Icon from 'sail/Icon';
import Demo from './Demo';
import Response from './Response';
import '../../css/Code.scss';
import {useGuide} from '../../data/GuideProvider';
import {useContent} from '../../data/ContentProvider';
import {useCopyText} from '../CopyTextButton';

import {logError} from '../../util';
import type {Files, Step} from '../../data/GuideTypes';
import {DownloadButtonLink} from '../DownloadButton';

type HighlightCodeProps = {|
  files: Files,
  lines?: {start: number, end: number},
  code: [string],
  highlights: {[number]: ['active' | 'added']},
  step: Step,
|};

function CopyFileButton({text}) {
  const copyButtonProps = useCopyText({text, source: 'copy_file_text_button'});
  const {activeStep} = useGuide();

  const handleClick = copyButtonProps.onClick;

  return (
    <UnstyledLink
      onClick={handleClick}
      className="CopyCodeButton"
      style={{bottom: activeStep ? 16 : 40}}
    >
      <Group alignItems="center" direction="horizontal" spacing={8}>
        <Icon icon={copyButtonProps.icon} size={12} color="gray800" />
        <Text color="gray800" weight="medium" lineHeight={20} size={14}>
          {copyButtonProps.label}
        </Text>
      </Group>
    </UnstyledLink>
  );
}

const HighlightCode = ({
  files,
  lines,
  code,
  highlights,
  step,
  hovered,
}: HighlightCodeProps) => {
  const [showResponse, setShowResponse] = useState(false);
  const [highlightNewLines, setHighlightNewLines] = useState(true);
  const {extensions} = useContent();
  const prevExtensions = useRef(extensions);
  const timeout = React.useRef();

  useEffect(() => {
    setShowResponse(false);
  }, [step]);

  useEffect(() => {
    // Run a quick animation to highlight any added lines of code
    // when we toggle an extension in the UI
    if (timeout.current) {
      clearTimeout(timeout.current);
    }

    const prev = prevExtensions.current;
    // Find the extension that was toggled by checking to see
    // if the ID was not in the previous set of extensions or
    // if it's value does not match the previous value for that extension ID
    const updatedExtension = Object.keys(extensions).filter(
      (id) => !(id in prev) || prev[id] !== extensions[id],
    )[0];
    prevExtensions.current = extensions;

    // Highlight the new lines only if we are toggling the extension on
    if (extensions[updatedExtension]) {
      setHighlightNewLines(true);
      timeout.current = setTimeout(() => {
        setHighlightNewLines(false);
        timeout.current = null;
      }, 500);
    }
  }, [extensions]);

  useEffect(() => {
    const container = document.querySelector('.code-view pre');
    // No need to scroll if we are at the same step or there
    // are no highlighted lines
    if (!lines || !container) {
      return;
    }

    if (lines.start && lines.end) {
      const codeEl = document.querySelector('.code-view pre code');
      if (!codeEl) {
        return;
      }
      const start = codeEl.children[lines.start - 1];
      if (!start) {
        // There is a bug in the code parsing and the line range for this step was off
        // causing it to try to select an Element that did not exist
        // (e.g. code.children[lines.start - 1] was greater than the list of children)
        logError({
          message: `Error when highlighting step-id "${
            step ? step.id : 'unknown'
          }" at line ${lines.start}`,
        });
        return;
      }

      const startPos = start.offsetTop;
      const end = codeEl.children[lines.end - 1];
      const endPos = end.offsetTop;

      const containerPos = container.scrollTop;
      const containerHeight = container.clientHeight;

      // if the starting or ending line is hidden
      // then scroll the code element to where the starting line
      // is (offset 20 for some padding)
      if (
        startPos < containerPos ||
        endPos + end.clientHeight + 20 > containerPos + containerHeight
      ) {
        container.scrollTop = start.offsetTop - 20;
      }
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */ // TODO: use correct dependencies
  }, [lines]);

  const handleEsc = (evt) => {
    if (evt.keyCode === 27) {
      setShowResponse(false);
    }
  };

  useEffect(() => {
    document.addEventListener('keydown', handleEsc);

    return () => {
      document.removeEventListener('keydown', handleEsc);
    };
  }, []);

  const handleCodeClick = (evt) => {
    const responseBtn = evt.target.classList.contains('response');
    if (responseBtn) {
      setShowResponse(!showResponse);
    }
  };

  const htmlEl = document.querySelector('html');
  if (!code || !htmlEl) {
    return null;
  }

  return (
    <>
      <pre
        className="Code"
        style={{
          opacity: 1,
          background: '#212D63',
          lineHeight: '20px',
          height: '100%',
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          marginBottom: '-1px', // Handles gap with Safari scrollbars
          marginRight: '-1px',
          scrollBehavior: 'smooth',
          position: 'relative',
        }}
      >
        <code
          style={{
            padding: '0',
            paddingBottom: '24px',
            paddingTop: '12px',
            display: 'flex',
            flexDirection: 'column',
            scrollBehavior: 'smooth',
            position: 'absolute',
            top: 0,
            left: 0,
            color: '#f5fbff',
            minWidth: '100%',
          }}
        >
          {code.map((line, i) => {
            const hightlight = highlights[i + 1] || [];
            return (
              <Line
                content={line}
                key={`Line-${i}`}
                index={i}
                highlight={hightlight}
                highlightNewLines={highlightNewLines}
                fade={step && !hovered && !hightlight.includes('active')}
                handleCodeClick={handleCodeClick}
              />
            );
          })}
        </code>
      </pre>
      {step && step.response && (
        <Response
          show={showResponse}
          handleClose={(show) => {
            setShowResponse(show);
          }}
        />
      )}
    </>
  );
};

type LineProps = {|
  content: string,
  index: number,
  highlight: ['active' | 'added' | null],
  highlightNewLines: boolean,
  fade: boolean,
  handleCodeClick: (SyntheticEvent<HTMLElement>) => void,
|};

/* Represents a single line of code that is rendered in the UI */
const Line = ({
  content,
  index,
  highlight,
  highlightNewLines,
  fade,
  handleCodeClick,
}: LineProps) => {
  // Highlight adds a different color background depending on the state
  const active = highlight.includes('active');
  const recentlyAdded = highlight.includes('added');

  return (
    <div
      key={`line-${index}`}
      className={classNames('line', {
        added: recentlyAdded && highlightNewLines,
        active,
        fade: !active && fade,
      })}
      onClick={active ? handleCodeClick : null}
      role="button"
    >
      <span
        className="lineNumber"
        style={{
          minWidth: '40px',
          display: 'inline-block',
          textAlign: 'right',
          marginRight: '20px',
          position: 'relative',
          color: 'rgba(255, 255, 255, 0.3)',
          userSelect: 'none',
        }}
      >
        {index + 1}
      </span>
      {/*  This content only comes from source code, never user input. */}
      {/* eslint-disable-next-line react/no-danger */}
      <span dangerouslySetInnerHTML={{__html: content}} />
    </div>
  );
};

function File({file: {name, label}, isActive, handleClick}) {
  const path = name && name.split('/');
  const filename = path && path.length > 1 ? path[path.length - 1] : name;
  return (
    <Box flex={{alignItems: 'center'}}>
      <Group direction="horizontal" alignItems="center">
        <ButtonLink
          className={isActive ? 'file-on' : 'file-off'}
          icon="document"
          size="small"
          label={label || filename}
          style={{
            padding: '2px 6px',
            marginLeft: '-6px',
            cursor: isActive ? 'default' : 'pointer',
            background: isActive ? '#A4CDFE' : 'transparent',
            borderRadius: '12px',
          }}
          onClick={handleClick}
        />
      </Group>
    </Box>
  );
}

const FileHeader = ({activeFile}) => {
  const {setFile, activeDemo, activeStep} = useGuide();
  const {files} = useContent();
  const [hovered, setHovered] = useState(false);

  // Keep track of which lines have highlights (e.g. active step)
  const highlights = {};
  let lines;
  const {lines: code, linesAdded, raw} = files[activeFile];

  if (activeStep && activeStep.lines) {
    ({lines} = activeStep);
    const start = lines.start || 0;
    const end = lines.end || 1;
    // Create the list of lines that need to be highlighted
    new Array(end - start + 1).fill().forEach((v, i) => {
      highlights[i + start] = ['active'];
    });
  }

  // linesAdded has the set of lines that were recently added by toggling on an extension
  // Highlight those lines green
  linesAdded.forEach((i) => {
    if (highlights[i + 1]) {
      highlights[i + 1].push('added');
    } else {
      highlights[i + 1] = ['added'];
    }
  });

  // TODO: Remove LegacyContentWrapper class when we generalize
  // Prism css styles
  return (
    <Box
      onMouseEnter={() => {
        if (document.querySelector('.Guide-rightcol').offsetTop > 96) {
          // Prevent scrolling the Content only once we've scrolled far enough down
          // the page to center the right column content
          document.querySelector('.Content').style['overflow-y'] = 'hidden';
        }
        setHovered(true);
      }}
      onMouseLeave={() => {
        document.querySelector('.Content').style['overflow-y'] = 'auto';
        setHovered(false);
      }}
      className="code-view LegacyContentWrapper"
      data-lang={activeFile}
      flex={{direction: 'column'}}
      style={{
        height: '100%',
        position: 'relative',
        background: '#212D63',
        overflow: 'hidden',
        scrollBehavior: 'smooth',
        fontFamily: 'Menlo Regular, monospace',
        borderRadius: activeDemo ? '0 0 6px 6px' : '6px',
      }}
    >
      <Box
        flex={{justifyContent: 'space-between', alignItems: 'center'}}
        style={{
          position: 'sticky',
          top: '0',
          zIndex: '99',
          backgroundColor: '#212D63',
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
          marginBottom: '1px', // Prevents border from being hidden on hover
        }}
      >
        <Box
          padding={{top: 16, right: 24, left: 24, bottom: 12}}
          style={{overflowX: 'auto', overflowY: 'hidden'}}
        >
          <Group direction="horizontal" spacing={16} margin={{right: 16}}>
            {Object.keys(files).map((id) => {
              return (
                <File
                  key={files[id].name}
                  file={files[id]}
                  isActive={activeFile === id}
                  handleClick={(evt) => {
                    setFile(id)();
                  }}
                />
              );
            })}
          </Group>
        </Box>
        <Box
          style={{minWidth: '150px', textAlign: 'right'}}
          padding={{top: 16, right: 16, left: 24, bottom: 12}}
        >
          <DownloadButtonLink />
        </Box>
      </Box>
      <HighlightCode
        files={files}
        code={code}
        lines={lines}
        highlights={highlights}
        step={activeStep}
        hovered={hovered}
      />
      <CopyFileButton text={raw} />
    </Box>
  );
};

const pickFileToDisplay = ({activeStep, activeFile, files}) => {
  // If a step is active, use the file that the step references
  if (activeStep && activeStep.file) {
    return activeStep.file;
  }
  // If a user is exploring the code (so no step is active)
  // then use the file they've selected
  if (activeFile) {
    return activeFile;
  }
  // Otherwise, pick the first available file in the set
  // e.g. on page load
  if (files && Object.keys(files).length) {
    return Object.keys(files)[0];
  }

  return null;
};

export default function Code() {
  const {activeFile, activeStep} = useGuide();
  const {files} = useContent();

  if (!files) {
    return null;
  }
  const filename = pickFileToDisplay({activeStep, activeFile, files});

  if (!filename || !files[filename]) {
    // TODO: Fix the initial render so we don't get here
    return null;
  }

  return (
    <Box flex={{direction: 'column'}} style={{height: '100%'}}>
      <Demo />
      <FileHeader activeFile={filename} />
    </Box>
  );
}
